// Casino Dashboard › Daily Transaction
//
// Ported from casino_dashboard/src/layouts/dailytransactions — the day-by-day
// totals from GET /dashboard/totaltransaction/{days}, with PDF and CSV exports.
import React from "react";
import { Box, Button, Snackbar, Alert } from "@mui/material";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import DataTable from "../../components/DataTable";
import DashboardService from "../../Utility/dashboardService";
import { ProfitChip, StateLine, errText, fixed } from "./shared";

// The original page loads (and exports) this fixed bucket rather than exposing
// the period buttons the sibling pages use.
const BUCKET = 9;

export default function CasinoDailyTransactions() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [downloading, setDownloading] = React.useState(null);
  const [snack, setSnack] = React.useState(null);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    setRows([]);
    try {
      const result = await DashboardService.getData(`/dashboard/totaltransaction/${BUCKET}`);
      setRows(Array.isArray(result?.data) ? result.data : []);
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const download = async (code) => {
    setDownloading(code);
    try {
      await DashboardService.downloadFile(
        `/totaltransaction/download/${BUCKET}/${code}`,
        `totaltransaction.${code}`
      );
      setSnack({ text: `${code.toUpperCase()} downloaded.`, sev: "success" });
    } catch (e) {
      setSnack({ text: errText(e), sev: "error" });
    } finally {
      setDownloading(null);
    }
  };

  const tableRows = React.useMemo(
    () =>
      rows.map((t) => {
        const deposit = Number(t.totalDeposit);
        const credited =
          Number(t.totalScCoins) + Number(t.loginSC) + Number(t.postSC) + Number(t.mailSC);
        return {
          name: t.name,
          totalDeposit: `$${fixed(t.totalDeposit)}`,
          totalScCoins: fixed(t.totalScCoins),
          totalWithdrawal: `$${fixed(t.totalWithdrawal)}`,
          loginSC: fixed(t.loginSC),
          signupSC: fixed(t.signupSC),
          referSC: fixed(t.referSC),
          referCashback: fixed(t.referCashback),
          transferSC: fixed(t.transferSC),
          gctoSC: fixed(t.gctoSC),
          postSC: fixed(t.postSC),
          mailSC: fixed(t.mailSC),
          expiredSC: fixed(t.expiredSC),
          profit: deposit ? fixed((credited / deposit) * 100) : "0.00",
          payBets: `$${fixed(t.payBets)}`,
          winBets: `$${fixed(t.winBets)}`,
        };
      }),
    [rows]
  );

  const columns = [
    { header: "Date", accessor: "name", align: "left" },
    { header: "Deposit", accessor: "totalDeposit", align: "center" },
    { header: "SC Buy", accessor: "totalScCoins", align: "center" },
    { header: "Withdrawal", accessor: "totalWithdrawal", align: "center" },
    { header: "Login SC", accessor: "loginSC", align: "center" },
    { header: "Signup SC", accessor: "signupSC", align: "center" },
    { header: "Refer SC", accessor: "referSC", align: "center" },
    { header: "RAF Cashback", accessor: "referCashback", align: "center" },
    { header: "Transfer SC", accessor: "transferSC", align: "center" },
    { header: "GC to SC", accessor: "gctoSC", align: "center" },
    { header: "Post SC", accessor: "postSC", align: "center" },
    { header: "Mail SC", accessor: "mailSC", align: "center" },
    { header: "Expired SC", accessor: "expiredSC", align: "center" },
    {
      header: "Profit/Loss",
      accessor: "profit",
      align: "center",
      // Healthy when credited coins stay within 110% of the money taken.
      render: (v) => <ProfitChip value={v} suffix="%" positive={Number(v) - 100 <= 10} />,
    },
    { header: "Pay Bets", accessor: "payBets", align: "center" },
    { header: "Win Bets", accessor: "winBets", align: "center" },
  ];

  return (
    <Box>
      <PageHeader
        title="Daily Transaction"
        crumb="Casino Dashboard / Daily Transaction"
        subtitle="Day-by-day deposits, coin grants and game play"
        actions={
          <>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={() => download("pdf")}
              disabled={!!downloading}
            >
              {downloading === "pdf" ? "Downloading…" : "PDF"}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<FileDownloadRoundedIcon />}
              onClick={() => download("csv")}
              disabled={!!downloading}
            >
              {downloading === "csv" ? "Downloading…" : "CSV"}
            </Button>
          </>
        }
      />

      <SectionCard title="Transactions" sx={{ p: 2, height: "auto" }}>
        <StateLine error={error} />
        {!error && (
          <DataTable
            columns={columns}
            rows={tableRows}
            loading={loading}
            searchable
            sortable
            paginated
            entriesPerPage
            showTotalEntries
            searchPlaceholder="Search date…"
            emptyText="No daily totals available."
          />
        )}
      </SectionCard>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack?.sev || "success"} onClose={() => setSnack(null)} variant="filled">
          {snack?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
